import { makeAutoObservable } from 'mobx'
import { isCancel } from 'axios'
import { RequestCancelAxios } from 'shared/api/lib'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { MessagesAnalyticsApi } from 'entities/Analytics/api/messagesAnalytics'
import { type MessagingAnalyticsKeys } from 'entities/Analytics/api/types'
import { RowMessagesComparison } from 'entities/Analytics/model/Table/RowMessagesComparison'
import { SearchTypesEnum } from 'widgets/SearchDropdown'
import { ExportAnalyticsModalStore } from 'widgets/ExportAnalyticsModal'
import { MessagesChartDataStore } from 'pages/analytics/pages/messages/ui/Chart'
import { AnalyticsFiltersStore } from 'pages/analytics/store/analyticsFiltersStore'
import {
  AnalyticsComparisonTableStore,
  type IAnalyticsComparisonTableConfig,
} from 'pages/analytics/store/analyticsComparisonTableStore'

export type MessagesComparisonTableStore = AnalyticsComparisonTableStore<
  MessagingAnalyticsKeys,
  RowMessagesComparison
>

export type IMessagesComparisonTableConfig = IAnalyticsComparisonTableConfig<
  MessagingAnalyticsKeys,
  RowMessagesComparison
>

export class MessagesAnalyticsPageStore {
  private readonly _chartDataStore: MessagesChartDataStore
  private readonly _analyticsFiltersStore: AnalyticsFiltersStore
  private readonly _exportAnalyticsModalStore: ExportAnalyticsModalStore
  private readonly _comparisonTableStore: MessagesComparisonTableStore
  private readonly _requestCancelAxios = new RequestCancelAxios()

  private _error: string | null = null
  private _pageLoading = false
  private _chartLoading = false

  constructor(entities: IEntitiesStore) {
    this._chartDataStore = new MessagesChartDataStore()
    this._analyticsFiltersStore = new AnalyticsFiltersStore(entities, {
      onFiltersChange: (loadingType) => this._fetchAnalytics(loadingType),
      additionalFilterTypes: [
        SearchTypesEnum.members,
        SearchTypesEnum.teams,
        SearchTypesEnum.numbers,
        SearchTypesEnum.outbound_sources,
        SearchTypesEnum.time_range,
      ],
      breakdownFilterTypes: [
        SearchTypesEnum.members,
        SearchTypesEnum.teams,
        SearchTypesEnum.numbers,
        SearchTypesEnum.outbound_sources,
      ],
    })
    this._exportAnalyticsModalStore = new ExportAnalyticsModalStore(
      MessagesAnalyticsApi.exportAnalytics
    )

    const comparisonTableConfig: IMessagesComparisonTableConfig = {
      tableKey: 'messagesComparisonTable',
      getAnalytics: MessagesAnalyticsApi.getAnalytics,
      createRow: (data) => new RowMessagesComparison(data),
      createMetricsRecord: () => ({
        total: 0,
        total_inbound: 0,
        total_outbound: 0,
        delivered: 0,
        undelivered: 0,
        carrier_violations: 0,
        optouts: 0,
        total_rvm: 0,
        delivered_rvm: 0,
        undelivered_rvm: 0,
      }),
    }

    this._comparisonTableStore = new AnalyticsComparisonTableStore(
      comparisonTableConfig,
      () => this._analyticsFiltersStore.analyticsFiltersParams,
      () => this._analyticsFiltersStore.breakdownFilterStore.appliedBreakdownFilterType,
      entities
    )
    makeAutoObservable(this)
  }

  init = () => {
    this._analyticsFiltersStore.initReactions()
    this._comparisonTableStore.init()
    this._fetchAnalytics('page')
  }

  openExportModal = () => {
    const { analyticsFiltersParams } = this._analyticsFiltersStore
    this._exportAnalyticsModalStore.onOpenExport(analyticsFiltersParams)
  }

  private _fetchAnalytics = async (loadingType: 'page' | 'chart' = 'page') => {
    if (loadingType === 'page') {
      this._pageLoading = true
    } else {
      this._chartLoading = true
    }

    try {
      this._error = null

      const { analyticsFiltersParams } = this._analyticsFiltersStore
      const { period } = this._analyticsFiltersStore.chartFilterStore

      this._requestCancelAxios.init()

      await MessagesAnalyticsApi.preloadAnalytics(analyticsFiltersParams, {
        cancelToken: this._requestCancelAxios.token,
      })

      const response = await MessagesAnalyticsApi.getAnalytics(analyticsFiltersParams, {
        cancelToken: this._requestCancelAxios.token,
      })

      this._chartDataStore.setRawData(response.data, period)
    } catch (error) {
      if (!isCancel(error)) {
        console.error('Analytics fetch error:', error)
        this._error = 'Failed to load analytics data'
      }
    } finally {
      if (loadingType === 'page') {
        this._pageLoading = false
      } else {
        this._chartLoading = false
      }
    }
  }

  get calendarFilterStore() {
    return this._analyticsFiltersStore.calendarFilterStore
  }

  get breakdownFilterStore() {
    return this._analyticsFiltersStore.breakdownFilterStore
  }

  get chartFilterStore() {
    return this._analyticsFiltersStore.chartFilterStore
  }

  get chartDataStore() {
    return this._chartDataStore
  }

  get comparisonTableStore() {
    return this._comparisonTableStore
  }

  get pageLoading() {
    return this._pageLoading
  }

  get chartLoading() {
    return this._chartLoading
  }

  get error() {
    return this._error
  }

  dispose = () => {
    this._analyticsFiltersStore.dispose()
    this._requestCancelAxios.reset()
    this._chartDataStore.dispose?.()
    this._comparisonTableStore.dispose()
    this._exportAnalyticsModalStore.dispose()
  }
}
